<template>
  <div
    id="rideEstimateSection"
    class="state"
  >
    <div
      v-if="shouldDisplaySurgeConfirm"
      class="row order-ride-info"
    >
      <label class="col-4">Surge: </label>
      <div class="col-8">
        <span
          v-if="estimationRide.surgeFactor > 1"
          id="surgeFactor"
          class="spanText"
        >{{ estimationRide.surgeFactor.toFixed(1) }} x <br></span>
        <p
          v-if="isFamilyDashboard"
          class="mt-2 text-red"
        >
          PLEASE VERIFY SURGE AMOUNT BEFORE ORDERING THIS RIDE
        </p>
        <p
          v-else
          class="mt-2 text-red"
        >
          PLEASE VERIFY SURGE AMOUNT WITH THE CALLER BEFORE ORDERING THIS RIDE
        </p>
        <p
          v-if="estimationRide.pricingExplanation"
          class="notification-message"
        >
          {{ estimationRide.pricingExplanation }}
        </p>
        <el-checkbox
          :model-value="operatorConfirmedSurgePriceWithCustomer"
          @update:model-value="emit('update:operatorConfirmedSurgePriceWithCustomer', $event)"
        >
          <span v-if="isFamilyDashboard">
            Please confirm that you understand and accept the surge pricing for this ride by checking this box.
          </span>
          <span v-else> I confirmed the surge price with the customer </span>
        </el-checkbox>
      </div>
    </div>

    <div
      v-if="preferredContactWarning"
      class="row order-ride-info"
    >
      <label class="col-4 notification-message"> Warning:&nbsp; </label>
      <span class="col-8 notification-message">
        {{ preferredContactWarning }}
      </span>
    </div>

    <div
      v-if="estimationRide.warningMessage && isOperatorDashboard"
      class="row order-ride-info"
    >
      <label class="col-4 notification-message">Warning:&nbsp;</label>
      <span
        v-dompurify-html="util.convertTextToHtml(estimationRide.warningMessage)"
        class="col-8 notification-message"
      />
    </div>

    <div
      v-if="estimationRide.fareDisplay"
      class="row order-ride-info"
    >
      <label class="col-4">Fare Display: </label>
      <div class="col-8">
        {{ estimationRide.fareDisplay }}
      </div>
    </div>

    <div
      v-if="estimationRide.multipler"
      class="row order-ride-info"
    >
      <label class="col-4">Multipler: </label>
      <div class="col-8">
        {{ estimationRide.multipler }}
      </div>
    </div>

    <div
      v-if="estimationRide.minutesToAutoRetryRequest"
      class="row order-ride-info align-items-end"
    >
      <label class="col-4">Auto Retry Getting a Driver for: </label>
      <span class="col-8">
        {{ estimationRide.minutesToAutoRetryRequest }}
      </span>
    </div>

    <div class="row order-ride-info">
      <label class="col-4">Ride Type: </label>
      <span class="col-8 notification-message text-capitalize"> {{ estimationRide.rideType }} for {{ estimationRide.riderName }} </span>
    </div>

    <div
      v-if="shouldDisplayEta"
      class="row order-ride-info"
    >
      <label class="col-4">ETA: </label>
      <span class="col-8">
        {{ estimationRide.etaText }}
      </span>
    </div>

    <div
      v-if="shouldDisplayRideDetails && shouldShowPartnerSubsidy"
      class="row order-ride-info"
    >
      <label class="col-4 text-capitalize">Estimate with Partner Subsidy Applied: </label>
      <div class="col-8">
        <p :class="{ 'fully-subsidized-bold': isFullySubsidized }">
          ${{ estimationRide.highEstimate - estimationRide.partnerSubsidyEstimate }}
          <template v-if="isFullySubsidized"> (fully covered by partner)</template>
        </p>
      </div>
    </div>

    <div
      v-if="driverAvailabilityText"
      class="row order-ride-info"
    >
      <label class="col-4">Driver Availability at Scheduled Time: </label>
      <span class="col-8">
        {{ driverAvailabilityText }}
      </span>
    </div>

    <div v-if="shouldDisplayRideDetails">
      <div class="row order-ride-info">
        <label class="col-4">Ride Details: </label>
        <div class="col-8">
          <template v-if="Number(estimationRide.surgeFactor) > 1 && !isForCvrcAcrcOrionisoCustomer">
            <p>
              Standard Price: A {{ estimationRide.duration }} minute(s) trip over&nbsp; {{ distanceByUnits }} {{ distanceUnit }} for&nbsp;
              <span :class="{ 'fully-subsidized-estimate': isFullySubsidized }">{{ estimationRide.standardEstimate }}</span>
            </p>
            <p>
              Surge Price: A {{ estimationRide.duration }} minute(s) trip over&nbsp; {{ distanceByUnits }} {{ distanceUnit }} for&nbsp;
              <span :class="{ 'fully-subsidized-estimate': isFullySubsidized }">{{ estimateText }}</span>
            </p>
          </template>
          <p v-else>
            A {{ estimationRide.duration }} minute(s) trip over&nbsp; {{ distanceByUnits }} {{ distanceUnit }}
            <template v-if="showFullDetail">
              for&nbsp;
              <span :class="{ 'fully-subsidized-estimate': isFullySubsidized }">{{ estimateText }}</span>
            </template>
          </p>
          <template v-if="!isFamilyDashboard && showFullDetail">
            <p v-if="estimationRide.operatingFee">
              Operating Fee: {{ estimationRide.operatingFee }} $
            </p>
            <p>Surge Factor: {{ estimationRide.surgeFactor }}</p>
            <p>Cost Per {{ costPerUnitText }}: {{ costPerDistance ? `$ ${costPerDistance}` : '' }}</p>
            <p>Cost Per Minute: {{ estimationRide.perMinute ? `$ ${estimationRide.perMinute}` : '' }}</p>
            <p>Base Fare: {{ estimationRide.baseFare ? `${estimationRide.baseFare}` : '' }}</p>
            <p>Minimum Fare: {{ estimationRide.minimumFare ? `${estimationRide.minimumFare}` : '' }}</p>
          </template>
        </div>
      </div>

      <div
        v-if="shouldShowPartnerCost"
        class="row order-ride-info"
      >
        <label class="col-4 text-capitalize">Partner Cost:</label>
        <div class="col-8">
          <p>$ {{ estimationRide.partnerCostEstimate?.toFixed(2) }}</p>
        </div>
      </div>

      <div
        v-if="showFullDetail && !isForCvrcAcrcOrionisoCustomer"
        class="row order-ride-info"
      >
        <label class="col-4 text-capitalize">GoGo Cost</label>
        <div class="col-8">
          <p :class="{ 'fully-subsidized-estimate': isFullySubsidized }">{{ estimateText }}</p>
        </div>
      </div>

      <template v-if="shouldShowPartnerSubsidy">
        <div class="row order-ride-info">
          <label class="col-4 text-capitalize">Estimated Partner Subsidy: </label>
          <div class="col-8">
            <p>${{ estimationRide.partnerSubsidyEstimate }}</p>
          </div>
        </div>
      </template>

      <template v-if="!isForCvrcAcrcOrionisoCustomer">
        <div class="row order-ride-info">
          <label class="col-4">Caller GoGo Credits: </label>
          <div class="col-8">
            {{ estimationRide.credits ? '$' + estimationRide.credits : '' }}
          </div>
        </div>

        <div
          v-if="!estimationRide.expiringCreditInfo?.showMembershipCreditBreakdown"
          class="row order-ride-info"
        >
          <label class="col-4">Caller Expiring Credits: </label>
          <div
            class="col-8"
            :class="{ ecAttention: hasExpiringCreditsAttention }"
          >
            {{ expiringCreditInfoText }}
          </div>
        </div>

        <template v-if="estimationRide.expiringCreditInfo?.showMembershipCreditBreakdown">
          <div class="row order-ride-info">
            <label class="col-4">Covered by Credit: </label>
            <div class="col-8">
              <span class="fully-subsidized-bold">
                ${{ rideUtility.formatCreditRange(estimationRide.expiringCreditInfo.coveredByCreditMin, estimationRide.expiringCreditInfo.coveredByCreditMax) }}
                <template v-if="isValue60FullyCovered"> (full ride)</template>
              </span>
            </div>
          </div>

          <div class="row order-ride-info">
            <label class="col-4">Caller Pays: </label>
            <div class="col-8">
              <span :class="{ 'fully-subsidized-bold': isValue60FullyCovered, 'bold': true }">
                ${{ rideUtility.formatCreditRange(estimationRide.expiringCreditInfo.callerPaysMin, estimationRide.expiringCreditInfo.callerPaysMax) }}
              </span>
              <span
                v-if="estimationRide.partnerSubsidyEstimate || estimationRide.credits"
                class="notification-message"
              > (before partner subsidy / GoGo Credits)</span>
            </div>
          </div>
        </template>
      </template>

      <template v-if="estimationRide.isAutoTip">
        <div class="row order-ride-info">
          <label class="col-4">User Has Auto Tipping On: </label>
          <div class="col-8">
            Yes
          </div>
        </div>

        <div class="row order-ride-info">
          <label class="col-4">Auto Tip Amount: </label>
          <div class="col-8">
            ${{ estimationRide.lowAutoTipAmount }} ~ ${{ estimationRide.highAutoTipAmount }}
          </div>
        </div>
      </template>

      <div
        v-else
        class="row order-ride-info"
      >
        <label class="col-4">User Has Auto Tipping On: </label>
        <div class="col-8">
          No
        </div>
      </div>

      <div class="row order-ride-info">
        <label class="col-4">Caller Partner Subsidies: </label>
        <div
          v-dompurify-html="estimationRide.partnerSubsidy ?? ''"
          class="col-8"
        />
      </div>

      <div
        v-if="!isFamilyDashboard"
        class="row order-ride-info"
      >
        <label class="col-4">Partner Info for Operator: </label>
        <div
          v-dompurify-html="estimationRide.partnerInfoForOperator ?? ''"
          class="col-8"
        />
      </div>

      <template v-if="isScheduledRide">
        <div
          v-if="isFamilyDashboard"
          class="row order-ride-info pl-20 pr-20"
        >
          <div class="col-12 confirm-surge-container">
            <p>
              This estimate is not guaranteed. If ride-demand is higher than supply at the time the final order is placed, there may be a
              "surge price" added. This could double the price, or be a smaller effect.<br>When this happens, do you want us to call you
              for confirmation before placing the order?<br>If you pick this, and then don't answer the call, the car will never come.<br><br>Or
              should we just place/keep the order anyway (and send you a notification)?
            </p>
            <div>
              <el-radio
                v-model="callToConfirmSurgeRef"
                label="yes"
              >
                Call to confirm the price before placing the order
              </el-radio>
              <el-radio
                v-model="callToConfirmSurgeRef"
                label="no"
              >
                Do not call, keep the order
              </el-radio>
            </div>
          </div>
        </div>

        <div
          v-else
          class="row order-ride-info"
        >
          <label class="col-4">Note: </label>
          <div class="col-8 notification-message">
            Please note that the above is an estimate of the ride cost at the current moment and may not be reflective of how much the ride
            cost at the time of the scheduled ride (for example: rush hour and/or low number of drivers)
          </div>
        </div>
      </template>
    </div>

    <div class="form-submit-button-group">
      <slot name="form-actions" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, WritableComputedRef, type PropType } from 'vue';

import util from '@/Utils/util';
import { useDashboard } from '@/use/app';
import rideUtility from '@/Utils/rideUtility';
import { MEASUREMENT_SYSTEM } from '@/models/app';
import { usePartner } from '@/use/partner';
import { useCustomer } from '@/use/customer';
import { FulfillmentIndicator, type RideEstimate } from '@/models/ride';
import { selectedOfferPromo } from '@/services/operator-first-ride-offer-inventory/viewModel';

const props = defineProps({
  estimationRide: {
    type: Object as PropType<RideEstimate>,
    required: true,
  },
  showFullDetail: {
    type: Boolean,
    default: false,
  },
  preferredContactWarning: {
    type: String,
    default: '',
  },
  isScheduledRide: {
    type: Boolean,
    default: false,
  },
  callToConfirmSurge: {
    type: String,
    default: 'yes',
  },
  operatorConfirmedSurgePriceWithCustomer: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:callToConfirmSurge', 'update:operatorConfirmedSurgePriceWithCustomer']);

const { isFamilyDashboard, isOperatorDashboard, isPartnerDashboard } = useDashboard();

const { partnerCode, isCvrcOrAcrc } = usePartner();

const { customerPartner } = useCustomer();
const shouldDisplayPartnerCosts = customerPartner?.value?.shouldDisplayPartnerCosts ?? true;

const callToConfirmSurgeRef: WritableComputedRef<string> = computed({
  get() {
    return props.callToConfirmSurge;
  },
  set(value) {
    emit('update:callToConfirmSurge', value);
  },
});

const shouldDisplaySurgeConfirm = computed(() => {
  if (customerPartner?.value?.shouldDisplaySurge === false) {
    return false;
  }

  return props.estimationRide.surgeFactor > 1 || props.estimationRide.pricingExplanation;
});

const shouldDisplayEta = computed(() => {
  // Scheduled rides should never display ETA
  if (props.isScheduledRide) {
    return false;
  }

  // If not a family dashboard, always show ETA
  return !isFamilyDashboard.value;
});

const distanceByUnits = computed(() => rideUtility.convertMilesToTargetUnits(props.estimationRide.distance, props.estimationRide.units));

const distanceUnit = computed(() => rideUtility.getDistanceUnit(props.estimationRide.units));

const costPerDistance = computed(() => rideUtility.getCostPerDistance(props.estimationRide.perMile, props.estimationRide.units));

const costPerUnitText = computed(() => (props.estimationRide.units === MEASUREMENT_SYSTEM.METRIC ? 'Km' : 'Mile'));

const shouldDisplayRideDetails = computed(() => {
  const { duration, lowEstimate, highEstimate } = props.estimationRide;
  return distanceByUnits.value && duration && lowEstimate && highEstimate;
});

const isForCvrcAcrcOrionisoCustomer = computed(() => {
  const isMatchingDashboard = isPartnerDashboard.value && (isCvrcOrAcrc(partnerCode?.value) || partnerCode?.value === 'orioniso');
  const isMatchingCustomer = isCvrcOrAcrc(props.estimationRide.partnerCode) || props.estimationRide.partnerCode === 'orioniso';

  return isMatchingDashboard || isMatchingCustomer;
});

const shouldShowPartnerCost = computed(() => {
  if (isFamilyDashboard.value || !shouldDisplayPartnerCosts) {
    return false;
  }

  return props.estimationRide.partnerCostEstimate != null;
});

const hasExpiringCreditsAttention = computed(
  () => props.estimationRide.expiringCreditInfo?.minAmount && props.estimationRide.expiringCreditInfo?.maxAmount,
);

const isValue60FullyCovered = computed(() => props.estimationRide.expiringCreditInfo?.callerPaysMax === 0);

const estimateText = computed(() => rideUtility.getFinalEstimateTxt({
  rideEstimate: props.estimationRide,
  offeredPromoCost: selectedOfferPromo.value?.discount ?? 0,
}));

const expiringCreditInfoText = computed(() => {
  const { description } = props.estimationRide.expiringCreditInfo;

  if (description.startsWith('new_user_first_ride_10_offer')) {
    return 'This caller gets $10 off this ride';
  }

  return description;
});

const shouldShowPartnerSubsidy = computed(() => {
  if (!props.estimationRide.partnerSubsidyEstimate) {
    return false;
  }

  if (!shouldDisplayPartnerCosts) {
    return false;
  }

  if (isFamilyDashboard.value) {
    return false;
  }

  if (isForCvrcAcrcOrionisoCustomer.value) {
    return false;
  }

  return true;
});

const isFullySubsidized = computed(() => {
  const { partnerSubsidyEstimate, highEstimate } = props.estimationRide;

  if (!partnerSubsidyEstimate || !highEstimate) {
    return false;
  }

  return partnerSubsidyEstimate >= highEstimate;
});

const driverAvailabilityText = computed(() => {
  if (!props.isScheduledRide) {
    return '';
  }

  if (!props.estimationRide.fulfillmentIndicator) {
    return '';
  }

  let text: string;
  switch (props.estimationRide.fulfillmentIndicator.toLowerCase()) {
    case FulfillmentIndicator.GREEN:
      text = 'More than 90% confidence';
      break;
    case FulfillmentIndicator.YELLOW:
      text = '70% ~ 90% confidence';
      break;
    case FulfillmentIndicator.RED:
      text = 'Less than 70% confidence';
      break;
    default:
      text = 'Predictions not available';
      break;
  }

  return text;
});
</script>

<style lang="scss">
#rideEstimateSection {
  .confirm-surge-container {
    border: 1px solid #000;
    padding: 10px;

    .el-radio__label {
      font-weight: 300;
    }
  }
}

.ecAttention {
  border: 1px solid #f9e24a;
  border-radius: 0.3em;
  background-color: #fcf9c2;
  color: #824e0e;
  padding: 0.5em;
  font-weight: bold;
  flex-basis: content;
}

.bold {
  font-weight: bold;
}

.fully-subsidized-estimate {
  text-decoration: line-through;
}

.fully-subsidized-bold {
  font-weight: 700;
  color: #2e7d32;
}
</style>
